/**
 * Native shadows: one table that every native surface reads (DS-006).
 *
 * Web draws Figma's two-layer shadows. Native can draw one layer, so each key
 * has a tuned native value: the dominant web layer, iOS radius = CSS blur ÷ 2,
 * and an Android elevation. Deliberate exceptions the table keeps: bigcard's
 * opacity is .10 (web layer .08), and topbar/bottombar use y ±4 at 5% (web
 * layer ±8 at 4%). fab, fab-pressed and knob are black, as in Figma, not grey-900.
 * fab takes its second, ambient layer (y 4, blur 8, 15%) rather than the 30%
 * key layer (y 1, blur 3), and elevation 6 (Android's resting FAB): the key
 * layer would sit barely above fab-pressed, so pressing wouldn't lower it.
 *
 * Two readers, same values:
 * - `shadow-<key>` classes on native builds (the Tailwind preset generates
 *   them from this table, see `native-utilities.ts`);
 * - `nativeShadow(platform)` for inline styles.
 *
 * Usage:
 *   import { nativeShadow } from "...";
 *   const styles = nativeShadow(Platform.OS);
 *   <View style={styles.card} />
 */

type ShadowStyle =
  | {
      shadowColor?: string;
      shadowOffset?: { width: number; height: number };
      shadowOpacity?: number;
      shadowRadius?: number;
      elevation?: number;
    }
  | object;

type PlatformType = "ios" | "android" | "default";

type ShadowKeys =
  | "card"
  | "card-hovered"
  | "bigcard"
  | "bigcard-hovered"
  | "topbar"
  | "bottombar"
  | "modal"
  | "dropdown"
  | "fab"
  | "fab-pressed"
  | "knob";

export interface NativeShadowSpec {
  /** iOS shadowOffset.height */
  offsetY: number;
  /** iOS shadowOpacity of `color` */
  opacity: number;
  /** iOS shadow colour, when not `NATIVE_SHADOW_COLOR` */
  color?: string;
  /** iOS shadowRadius */
  radius: number;
  /** Android elevation; Android uses the system shadow colour */
  elevation: number;
}

export const NATIVE_SHADOW_COLOR = "#202327";

export const nativeShadowSpec: Record<ShadowKeys, NativeShadowSpec> = {
  card: { offsetY: 4, opacity: 0.08, radius: 6, elevation: 4 },
  "card-hovered": { offsetY: 12, opacity: 0.12, radius: 21, elevation: 12 },
  bigcard: { offsetY: 8, opacity: 0.1, radius: 16, elevation: 8 },
  "bigcard-hovered": { offsetY: 12, opacity: 0.12, radius: 21, elevation: 12 },
  topbar: { offsetY: 4, opacity: 0.05, radius: 8, elevation: 4 },
  bottombar: { offsetY: -4, opacity: 0.05, radius: 8, elevation: 4 },
  modal: { offsetY: 12, opacity: 0.12, radius: 21, elevation: 24 },
  dropdown: { offsetY: 12, opacity: 0.12, radius: 21, elevation: 24 },
  fab: { offsetY: 4, opacity: 0.15, radius: 4, elevation: 6, color: "#000000" },
  "fab-pressed": { offsetY: 1, opacity: 0.2, radius: 0.65, elevation: 2, color: "#000000" },
  knob: { offsetY: 1, opacity: 0.1, radius: 1.5, elevation: 1, color: "#000000" },
};

const mapSpec = (toStyle: (spec: NativeShadowSpec) => ShadowStyle) =>
  Object.fromEntries(
    Object.entries(nativeShadowSpec).map(([key, spec]) => [key, toStyle(spec)]),
  ) as Record<ShadowKeys, ShadowStyle>;

const platformShadows: Record<PlatformType, Record<ShadowKeys, ShadowStyle>> = {
  ios: mapSpec(({ offsetY, opacity, radius, color = NATIVE_SHADOW_COLOR }) => ({
    shadowColor: color,
    shadowOffset: { width: 0, height: offsetY },
    shadowOpacity: opacity,
    shadowRadius: radius,
  })),
  android: mapSpec(({ elevation }) => ({ elevation })),
  default: mapSpec(() => ({})),
};

/**
 * Obtain platform-aware shadow style object mapping.
 * @param platform "ios" | "android" | "default"
 * @returns Record<ShadowKey, ShadowStyle>
 */
export function nativeShadow(platform: PlatformType) {
  // fallback to 'default' for unknown platforms
  return platformShadows[platform] || platformShadows.default;
}

export type NativeShadowKey = ShadowKeys;
